/**
 * /billing/kitchen data layer (Plan 20).
 *
 * REST surface served by `apps/ordo/server/src/routes/billing-kitchen.ts`
 * — TanStack Query is the only access pattern in the client per the
 * apps/ordo policy split. See `apps/ordo/CLAUDE.md` § "API style".
 *
 * Hooks:
 *   - useBillingKitchenSummaryQuery(filter) — page payload
 *     (permittedVendors, nextPayout, history) for the KitchenPayoutsPage.
 *   - useBillingKitchenPayoutDetailQuery(payoutId) — drawer payload
 *     (envelope + lines).
 *   - useBillingKitchenPayRatesQuery(filter) — "My pay rates" panel
 *     payload (per-item chef pay + upcoming rate changes). Never
 *     carries customer prices or margin — AP-side numbers only.
 *
 * Types mirror the server-side `KitchenPayoutsPagePayload` /
 * `KitchenPayoutRow` shapes so the components can import directly
 * from here.
 */

import { useQuery } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — kept structurally identical to the server-side response shape
// ---------------------------------------------------------------------------

export type PayoutStatus = 'PENDING' | 'BILLED' | 'SENT' | 'PAID' | 'FAILED' | 'HELD'

export type VendorPayoutLineKind =
  | 'ITEM'
  | 'CARVE_OUT'
  | 'ADJUSTMENT'
  | 'CLAWBACK'
  | 'VENDOR_SERVICE_FEE'
  | 'RATE_CORRECTION'

export interface PermittedKitchenVendor {
  organizationId: string
  organizationName: string
  profileCodes: ReadonlyArray<'CHEF_ADMIN' | 'CHEF_STAFF' | 'SUPPLIER_ADMIN' | 'SUPPLIER_STAFF'>
}

export interface KitchenPayoutRow {
  id: string
  servicingVendorId: string
  cycleDate: string
  /** Inclusive delivery-date window this envelope settles. `cycleDate` is only its upper bound. */
  periodStart: string | null
  periodEnd: string | null
  /** Vendor debt this cycle could not absorb, carried to the next one. */
  carriedBalanceCents: number
  status: PayoutStatus
  totalCents: number
  currency: string
  tipaltiPaymentId: string | null
  sentAt: string | null
  paidAt: string | null
  failedAt: string | null
  failureReason: string | null
  heldReason: string | null
  createdAt: string
  lineCount: number
}

export interface KitchenPayoutLineRow {
  id: string
  payoutId: string
  kind: VendorPayoutLineKind
  amountCents: number
  /** Grouped label ("Kitchen pay") — the text Tipalti prints, not the item. */
  description: string
  sourceOrderItemId: string | null
  createdAt: string
  /** Pays for a chef DRY RUN (a pre-launch test cook), not live service. */
  isDryRun: boolean
  /** The ITEM this line paid for. All four are null on a line with no source item. */
  itemName: string | null
  quantity: number | null
  schoolName: string | null
  /** `YYYY-MM-DD` the food reached the school. NOT the service or cycle date. */
  deliveryDate: string | null
  /** `YYYY-MM-DD` the food was SERVED — the day cooked, beside the delivery date. */
  serviceDate: string | null
}

export interface KitchenPayoutDetail {
  payout: KitchenPayoutRow
  lines: KitchenPayoutLineRow[]
}

export interface KitchenPayoutsPagePayload {
  permittedVendors: PermittedKitchenVendor[]
  activeVendorId: string | null
  nextPayout: KitchenPayoutDetail | null
  history: KitchenPayoutRow[]
  currency: string
}

export interface KitchenPayoutsFilter {
  /** Vendor narrow. Optional; defaults to the first permitted vendor server-side. */
  servicingVendorId?: string | null
}

export interface KitchenPayRateUpcoming {
  effectiveFrom: string
  vendorPayCents: number
  menuScoped: boolean
}

export interface KitchenPayRateRow {
  menuId: string
  menuName: string
  schoolName: string
  menuItemId: string
  itemName: string
  vendorPayCents: number
  isCosted: boolean
  currency: string
  upcoming: KitchenPayRateUpcoming[]
}

export interface KitchenPayRatesPayload {
  rates: KitchenPayRateRow[]
  currency: string
}

// ---------------------------------------------------------------------------
// Fetchers
// ---------------------------------------------------------------------------

export async function fetchBillingKitchenSummary(
  filter: KitchenPayoutsFilter,
  signal?: AbortSignal,
): Promise<KitchenPayoutsPagePayload> {
  return apiFetch<KitchenPayoutsPagePayload>('/api/billing/kitchen/summary', {
    query: { servicingVendorId: filter.servicingVendorId ?? undefined },
    signal,
  })
}

export async function fetchBillingKitchenPayoutDetail(
  payoutId: string,
  signal?: AbortSignal,
): Promise<KitchenPayoutDetail> {
  return apiFetch<KitchenPayoutDetail>(`/api/billing/kitchen/payouts/${encodeURIComponent(payoutId)}`, { signal })
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export const billingKitchenSummaryQueryKey = (filter: KitchenPayoutsFilter) =>
  ['billing', 'kitchen', 'summary', { servicingVendorId: filter.servicingVendorId ?? null }] as const

export function useBillingKitchenSummaryQuery(filter: KitchenPayoutsFilter = {}) {
  return useQuery<KitchenPayoutsPagePayload>({
    queryKey: billingKitchenSummaryQueryKey(filter),
    queryFn: ({ signal }) => fetchBillingKitchenSummary(filter, signal),
    staleTime: 30_000,
  })
}

export async function fetchBillingKitchenPayRates(
  filter: KitchenPayoutsFilter,
  signal?: AbortSignal,
): Promise<KitchenPayRatesPayload> {
  return apiFetch<KitchenPayRatesPayload>('/api/billing/kitchen/pay-rates', {
    query: { servicingVendorId: filter.servicingVendorId ?? undefined },
    signal,
  })
}

export const billingKitchenPayRatesQueryKey = (filter: KitchenPayoutsFilter) =>
  ['billing', 'kitchen', 'pay-rates', { servicingVendorId: filter.servicingVendorId ?? null }] as const

export function useBillingKitchenPayRatesQuery(filter: KitchenPayoutsFilter = {}) {
  return useQuery<KitchenPayRatesPayload>({
    queryKey: billingKitchenPayRatesQueryKey(filter),
    queryFn: ({ signal }) => fetchBillingKitchenPayRates(filter, signal),
    staleTime: 30_000,
  })
}

export const billingKitchenPayoutDetailKey = (payoutId: string | null) =>
  ['billing', 'kitchen', 'payout', payoutId] as const

export function useBillingKitchenPayoutDetailQuery(payoutId: string | null) {
  return useQuery<KitchenPayoutDetail>({
    queryKey: billingKitchenPayoutDetailKey(payoutId),
    queryFn: ({ signal }) => {
      if (!payoutId) {
        throw new Error('useBillingKitchenPayoutDetailQuery: payoutId is required')
      }
      return fetchBillingKitchenPayoutDetail(payoutId, signal)
    },
    enabled: Boolean(payoutId),
    staleTime: 30_000,
  })
}
